import { JourneyType } from '@bytel/leviator';

import { FaiType } from '@app-types/plan';
import { SimulatorCart, VirtualCartQuote } from '@app-types/simulator/virtual-cart';

import { getAppConfig } from './config';
import { getSessionVariable } from './storage';

const appConfig = getAppConfig();

export function getVirtualCart(): SimulatorCart {
    const cart = getSessionVariable('virtualCart');
    if (cart) {
        return JSON.parse(cart);
    }
    return createVirtualCart();
}

/**
 * Create virtual cart with default plan or with existing cart
 * @returns SimulatorCart
 */
export const createVirtualCart = (
    isFai: boolean = false,
    withExtraEmptyMobileJourney?: boolean,
    isConnected?: boolean,
    hasMobile: boolean = true,
    hasFai: boolean = true,
): SimulatorCart => {
    if (isConnected) {
        return {
            cart: {
                quotes: [
                    {
                        isCurrent: true,
                        products: [],
                        type: isFai ? JourneyType.FAI_ACQUISITION : JourneyType.MOBILE_ACQUISITION,
                    },
                ],
            },
        };
    }
    const cart: SimulatorCart = {
        cart: {
            quotes: [],
        },
    };

    if (hasFai) {
        cart.cart.quotes.push({
            isCurrent: !withExtraEmptyMobileJourney && isFai,
            products: [
                {
                    gencode: `${appConfig.defaultPlans.simulator.fai}`,
                },
            ],
            type: JourneyType.FAI_ACQUISITION,
        });
    }

    if (hasMobile) {
        cart.cart.quotes.push({
            isCurrent: !withExtraEmptyMobileJourney && !isFai,
            products: [
                {
                    gencode: `${appConfig.defaultPlans.simulator.mobile}`,
                },
            ],
            type: JourneyType.MOBILE_ACQUISITION,
        });
    }

    if (withExtraEmptyMobileJourney) {
        cart.cart.quotes.push({
            isCurrent: true,
            products: [],
            type: JourneyType.MOBILE_ACQUISITION,
        });
    }

    return cart;
};

/**
 * Mise à jours des parcours mobiles
 * @param currentCart panier virtuel
 * @param products Produits à simuler
 */
export const updateMobileQuotes = (
    currentCart: SimulatorCart,
    products: { gencode: string; index: number; hasIncitationPortability?: boolean }[],
): SimulatorCart => {
    const faiQuote = currentCart.cart.quotes.filter((q) => q.type === JourneyType.FAI_ACQUISITION);
    const mobileQuotes: VirtualCartQuote[] = [];

    products.forEach((product) => {
        mobileQuotes.push({
            isCurrent: false,
            products: [
                {
                    gencode: product.gencode,
                    hasIncitationPortability: product.hasIncitationPortability,
                },
            ],
            type: JourneyType.MOBILE_ACQUISITION,
        });
    });

    if (mobileQuotes[0]) {
        mobileQuotes[0].isCurrent = true;
    }

    currentCart.cart.quotes = [...faiQuote, ...mobileQuotes];
    return currentCart;
};

export const updateFaiQuotes = (currentCart: SimulatorCart, plan: FaiType): void => {
    const faiQuote = currentCart.cart.quotes.find((quote) => quote.type === JourneyType.FAI_ACQUISITION);
    if (faiQuote) {
        faiQuote.isCurrent = true;
        faiQuote.products = [{ gencode: plan.gencode }];
    } else {
        currentCart.cart.quotes.push({
            isCurrent: true,
            products: [{ gencode: plan.gencode }],
            type: JourneyType.FAI_ACQUISITION,
        });
    }
};

/**
 * Check if cart has been updated to trigger refresh
 * @param cartToCompare
 * @param currentCart
 * @returns
 */
export const hasCartBeenUpdated = (cartToCompare: SimulatorCart, currentCart: SimulatorCart): boolean => {
    if (cartToCompare.cart.quotes.length !== currentCart.cart.quotes.length) {
        return true;
    }

    const cartToCompareProducts = cartToCompare.cart.quotes
        .map((quote) => quote.products.map((product) => product.gencode))
        .flat();
    const currentCartProducts = currentCart.cart.quotes
        .map((quote) => quote.products.map((product) => product.gencode))
        .flat();
    if (cartToCompareProducts.length === currentCartProducts.length) {
        return !cartToCompareProducts.every((p, index) => p === currentCartProducts[index]);
    }

    return true;
};
